import React, { FunctionComponent, useCallback, useEffect, useState } from 'react';
import { useRecoilState } from 'recoil';
import { Box, Button, Dialog, LinearProgress } from '@mui/material';

import useMobileDetect from '../../../hooks/useMobileDetect';
import { useProfileCompletionFlow } from '../../../hooks/useProfileCompletionFlow';
import { approvedApplicationState } from '../../../state/atoms/approvedApplication';
import { dashboardProfilePopupOpen } from '../../../state/atoms/dashboardProfilePopupOpen';
import { useUser } from '../../../state/user';
import palette from '../../../styles/palette';
import {
  SponsorshipApplicationBase,
  StudentSponsorshipApplication,
} from '../../../types/sponsorshipApplication';
import { SponsorshipApplicationStatus } from '../../../types/sponsorshipApplicationStatuses';

const ProfilePopupWrapperContainer: FunctionComponent = () => {
  const [ modalOpen, setModalOpen ] = useRecoilState(dashboardProfilePopupOpen);
  const [ approvedApplication, setApprovedApplication ] = useRecoilState(approvedApplicationState);
  const user = useUser();
  const isMobile = useMobileDetect();
  const [fallbackApp] = useState<StudentSponsorshipApplication>({} as StudentSponsorshipApplication);
  const { currentStep, stepsList, navigateNext, navigatePrevious } = useProfileCompletionFlow(approvedApplication ?? fallbackApp);

  const closeModal = useCallback(() => {
    setModalOpen(false);
  }, [setModalOpen]);

  useEffect(() => {
    if (user.contents?.sponsorshipApplications) {
      const accepted = user.contents.sponsorshipApplications.find(
        (s: SponsorshipApplicationBase) => s.status === SponsorshipApplicationStatus.ACCEPTED,
      );

      if (accepted) {
        setApprovedApplication(accepted as StudentSponsorshipApplication);
      }
    }
  }, [ setApprovedApplication, user.contents ]);

  if (!modalOpen) return null;

  if (!currentStep || !stepsList?.length) {
    return (
      <Dialog fullScreen={isMobile} fullWidth maxWidth='sm' onClose={closeModal} open={modalOpen}>
        <Box sx={{ p: 4, textAlign: 'center' }}>
          <p>Loading your sponsorship application...</p>
        </Box>
      </Dialog>
    );
  }

  const currentStepIndex = stepsList.findIndex((step) => step.key === currentStep.key);

  if (currentStepIndex < 0) {
    return (
      <Dialog fullScreen={isMobile} fullWidth maxWidth='sm' onClose={closeModal} open={modalOpen}>
        <Box sx={{ p: 4, textAlign: 'center' }}>
          <p>No matching step found. Please try again later.</p>
        </Box>
      </Dialog>
    );
  }

  const StepComponent = currentStep.component;
  const totalSteps = stepsList.length;

  const isFirstStep = currentStepIndex === 0;
  const isLastStep = currentStepIndex === totalSteps - 1;

  // Progress is calculated based on the current step index, excluding the first and last steps
  // To prevent division by zero, we ensure that there are at least 3 steps before calculating progress.
  const progress = !isFirstStep && totalSteps > 2
    ? (currentStepIndex / (totalSteps - 1)) * 100
    : 0;

  return (
    <Dialog
      fullScreen={!isFirstStep && isMobile}
      fullWidth
      maxWidth='sm'
      onClose={closeModal}
      open={modalOpen}
      sx={
        {
          backgroundColor: isFirstStep || !isMobile ? 'transparent' : palette.neutral.plum[200],
        }
      }
    >
      <Box px={4.5} py={5.5}>
        {/* Show "Save & Exit" and Progress Bar only on intermediate steps (not first or last) */}
        {
          !isFirstStep && !isLastStep && (
            <Box sx={{ mb: 2 }}>
              {
                !isMobile && (
                  <Box sx={{ display: 'flex', justifyContent: 'flex-end', mb: 1 }}>
                    <Button
                      onClick={closeModal}
                      sx={
                        {
                          color: palette.grey.A400,
                          '&:hover': {
                            color: palette.purple.dark,
                            backgroundColor: 'transparent',
                          },
                          paddingRight: 0,
                          padding: '4px 0',
                          textTransform: 'none',
                          backgroundColor: 'transparent',
                        }
                      }
                      variant='text'
                    >
                      Save & Exit
                    </Button>
                  </Box>
                )
              }
              <Box>
                <LinearProgress
                  sx={
                    {
                      height: 12,
                      borderRadius: 5,
                    }
                  }
                  value={progress}
                  variant='determinate'
                />
              </Box>
            </Box>
          )
        }
        <StepComponent
          closeModal={closeModal}
          description={currentStep.description}
          navigateNext={navigateNext}
          navigatePrevious={navigatePrevious}
          sponsorshipApplication={approvedApplication}
          title={currentStep.title}
        />
      </Box>
    </Dialog>
  );
};

export default ProfilePopupWrapperContainer;
